/**
 * Premium "Partner Service List" — staggered service cards with avatar header.
 *
 * Used as the second beat after a partner reveal: shows the partner's logo +
 * name + city compactly at the top, then 3-6 service / product cards drift
 * in one-by-one (each with index, optional kategorie tag, title, arrow). At
 * the end an Allyver-URL pill pulses.
 *
 * Layout-Varianten (über `inputs.extras.layout`):
 *   • "compact"   — Default. Statische Karten staggern sequentiell rein.
 *   • "accordion" — Karten klappen einzeln auf und zu, zeigen Details
 *                   (Beschreibung, Preis, Bild) pro Eintrag. Ideal für
 *                   2–5 Angebote mit Inhalt. Items akzeptieren das Format
 *                   `"Titel|Beschreibung|Preis|BildURL"` (Pipe-getrennt).
 *
 * Layout is 9:16-first (cards 90% width) and degrades cleanly to 1:1/16:9.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import type { Fragment } from "../../types.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  pickMediaSrc, sceneSfxAudio, type SceneSfxBeat, typeSize, weights, withAlpha,
  colors as themeColors,
} from "../../reel/sceneShared.js";

type ListLayout = "compact" | "accordion";

function resolveListLayout(raw: unknown): ListLayout {
  const s = String(raw ?? "compact").trim().toLowerCase();
  if (s === "accordion" || s === "expand" || s === "details") return "accordion";
  return "compact";
}

/**
 * Parsed Item-Struktur: erlaubt einfache Strings ("Titel") oder Pipe-Format
 * ("Titel|Beschreibung|Preis|BildURL") für die Accordion-Variante.
 */
type ParsedItem = {
  title: string;
  description?: string;
  price?: string;
  imageUrl?: string;
};

function parseItem(raw: string): ParsedItem {
  const parts = String(raw ?? "").split("|").map((s) => s.trim());
  return {
    title: parts[0] ?? "",
    description: parts[1] || undefined,
    price: parts[2] || undefined,
    imageUrl: parts[3] || undefined,
  };
}

export const partnerServiceList: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const name = (inputs.text ?? "JUSATECH").trim();
  const city = (inputs.subtext ?? "").trim();
  const url = (inputs.url ?? "allyver.com").trim();
  const kategorieTag = String(inputs.extras?.kategorieType ?? "DIENSTLEISTUNG").trim();
  const layout = resolveListLayout(inputs.extras?.layout);
  // Accordion limitiert auf 5 Items (max. ~16 s laufzeit); compact erlaubt 6.
  const maxItems = layout === "accordion" ? 5 : 6;
  const rawServices = (inputs.items ?? [
    "Premium Service 1",
    "Premium Service 2",
    "Premium Service 3",
  ]).slice(0, maxItems);
  const services = rawServices.map((s) => s); // legacy plain titles
  const parsedItems = rawServices.map(parseItem);
  const logoUrl = pickMediaSrc(inputs.media, 0);

  const headerLogoSize = isVertical ? 110 : 90;
  const headerNameSize = typeSize(isVertical ? "lg" : "md", ctx.format) * 0.75;
  const headerCitySize = typeSize("sm", ctx.format) * 0.75;
  const headerEyeSize = typeSize("xs", ctx.format) * 0.95;
  const cardTitleSize = typeSize("sm", ctx.format) * 0.95;
  const cardTagSize = typeSize("xs", ctx.format) * 0.78;
  const cardIdxSize = typeSize("md", ctx.format) * 0.55;
  const ctaSize = typeSize("sm", ctx.format) * 0.85;

  const t = makeTl(clipStartSec);
  const headId = `${clipPrefix}-head`;
  const headLogoId = `${clipPrefix}-hlogo`;
  const headNameId = `${clipPrefix}-hname`;
  const headCityId = `${clipPrefix}-hcity`;
  const headEyeId = `${clipPrefix}-heye`;
  const listId = `${clipPrefix}-list`;
  const cardIds = services.map((_, i) => `${clipPrefix}-card-${i}`);
  const ctaId = `${clipPrefix}-cta`;
  const ctaGlowId = `${clipPrefix}-cta-glow`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const headLogo = logoUrl
    ? `<img id="${headLogoId}" src="${escapeHtml(logoUrl)}" alt="logo" style="width:${headerLogoSize}px;height:${headerLogoSize}px;object-fit:contain;border-radius:24px;background:${withAlpha(themeColors.fg, 0.06)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};padding:10px;box-shadow:0 12px 30px rgba(0,0,0,0.45);" />`
    : `<div id="${headLogoId}" style="width:${headerLogoSize}px;height:${headerLogoSize}px;border-radius:24px;background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.6)} 100%);display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headerLogoSize * 0.5}px;color:${themeColors.bg};box-shadow:0 12px 30px ${withAlpha(accent, 0.55)};">${escapeHtml(name.charAt(0))}</div>`;

  const headerHtml = `<div id="${headId}" style="display:flex;align-items:center;gap:22px;width:100%;max-width:${isVertical ? 880 : 1180}px;padding:18px 22px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.07)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.16)};border-radius:28px;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);box-shadow:0 24px 60px rgba(0,0,0,0.4);">
    ${headLogo}
    <div style="display:flex;flex-direction:column;gap:6px;flex:1;min-width:0;">
      <div id="${headEyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headerEyeSize}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">— Allyver Partner</div>
      <div id="${headNameId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headerNameSize}px;line-height:1;letter-spacing:-1.5px;color:${themeColors.fg};">${escapeHtml(name)}</div>
      ${city ? `<div id="${headCityId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${headerCitySize}px;color:${withAlpha(themeColors.fg, 0.7)};display:flex;align-items:center;gap:6px;">
        <svg width="${Math.round(headerCitySize)}" height="${Math.round(headerCitySize)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        <span>${escapeHtml(city)}</span>
      </div>` : ""}
    </div>
  </div>`;

  // CTA-Pill (gleich für beide Layouts)
  const ctaHtml = `<div id="${ctaId}" style="position:relative;display:inline-flex;align-items:center;gap:14px;padding:${isVertical ? 20 : 16}px ${isVertical ? 38 : 30}px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 16px 42px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:2px;text-transform:uppercase;color:${themeColors.bg};">
    <div id="${ctaGlowId}" style="position:absolute;inset:-12px;border-radius:9999px;background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.5)} 0%, transparent 70%);filter:blur(8px);pointer-events:none;opacity:0;"></div>
    <svg width="${Math.round(ctaSize)}" height="${Math.round(ctaSize)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
    <span>${escapeHtml(url)}</span>
  </div>`;

  const padTopBot = isVertical ? 80 : 56;
  const padX = isVertical ? 60 : 80;

  // --- Layout-Variante wählen ---------------------------------------------
  const listFragment = layout === "accordion"
    ? buildAccordionList({
        clipPrefix, isVertical, accent, ctx, t, parsedItems, kategorieTag,
        listId, cardIds, ctaId, ctaGlowId,
        cardTitleSize, cardTagSize, cardIdxSize,
        clipDurationSec,
      })
    : buildCompactList({
        clipPrefix, isVertical, accent, ctx, t, parsedItems, kategorieTag,
        listId, cardIds, ctaId, ctaGlowId,
        cardTitleSize, cardTagSize, cardIdxSize,
        clipDurationSec,
      });

  // Wir verzichten in beiden Layouts auf das Raw-Services-Array — die `parsedItems`
  // liefern die korrekt entpackten Titel (Pipe-Format-Safe).
  void services;

  // Dezente SFX-Choreografie pro Layout — sehr leise, weit unter dem
  // Brand-Music-Bed (typ. 0.18). Volumes 0.10–0.25, Sounds: tick (Stagger),
  // click (Accordion-Open), pop (CTA), tap (Header). Stumm sobald
  // `extras.sfx === false` gesetzt ist.
  const itemCount = services.length;
  const sfxBeats: SceneSfxBeat[] = [
    { sound: "tap", atSec: 0.08, volume: 0.18, durSec: 0.5 },
  ];
  if (layout === "accordion") {
    const introAt = 0.45;
    const stagger = 0.16;
    const firstExpandAt = introAt + itemCount * stagger + 0.3;
    const ctaTailReserve = 1.6;
    const remaining = Math.max(1.0, clipDurationSec - firstExpandAt - ctaTailReserve);
    const perItem = Math.max(1.8, Math.min(3.2, remaining / Math.max(1, itemCount)));
    for (let i = 0; i < itemCount; i++) {
      sfxBeats.push({ sound: "tick", atSec: introAt + i * stagger, volume: 0.10, durSec: 0.25 });
    }
    for (let i = 0; i < itemCount; i++) {
      sfxBeats.push({ sound: "click", atSec: firstExpandAt + i * perItem, volume: 0.18, durSec: 0.35 });
    }
    const ctaAt = firstExpandAt + itemCount * perItem + 0.17;
    sfxBeats.push({ sound: "pop", atSec: ctaAt, volume: 0.24, durSec: 0.5 });
  } else {
    for (let i = 0; i < itemCount; i++) {
      sfxBeats.push({ sound: "tick", atSec: 0.55 + i * 0.18, volume: 0.10, durSec: 0.25 });
    }
    const ctaAt = 0.7 + itemCount * 0.18;
    sfxBeats.push({ sound: "pop", atSec: ctaAt + 0.02, volume: 0.24, durSec: 0.5 });
  }
  const sfxHtml = sceneSfxAudio({ clipPrefix, clipStartSec, inputs, beats: sfxBeats });

  const html = `<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 22 : 18}px;padding:${padTopBot}px ${padX}px;">
    ${headerHtml}
    ${listFragment.html}
    ${ctaHtml}
  </div>${sfxHtml ? `\n${sfxHtml}` : ""}`;

  const init = [
    gsapSetLine(`#${headId}`, { opacity: 0, y: -24 }),
    gsapSetLine(`#${ctaId}`, { opacity: 0, y: 24, scale: 0.92 }),
    gsapSetLine(`#${ctaGlowId}`, { opacity: 0 }),
    listFragment.init ?? "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.05),
    ...(listFragment.tweens ?? []),
  ];

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};

// ============================================================================
// SHARED LIST-LAYOUT CONTEXT
// ============================================================================
type ListLayoutCtx = {
  clipPrefix: string;
  isVertical: boolean;
  accent: string;
  ctx: Parameters<SceneBuilder>[0]["ctx"];
  t: ReturnType<typeof makeTl>;
  parsedItems: ParsedItem[];
  kategorieTag: string;
  listId: string;
  cardIds: string[];
  ctaId: string;
  ctaGlowId: string;
  cardTitleSize: number;
  cardTagSize: number;
  cardIdxSize: number;
  clipDurationSec: number;
};

// ============================================================================
// COMPACT LIST (statische Karten staggern rein) — Default-Verhalten
// ============================================================================
function buildCompactList(L: ListLayoutCtx): Fragment {
  const { isVertical, accent, ctx, t, parsedItems, kategorieTag, listId,
    cardIds, ctaId, ctaGlowId, cardTitleSize, cardTagSize, cardIdxSize,
    clipDurationSec } = L;

  const html = `<div id="${listId}" style="display:flex;flex-direction:column;gap:14px;width:100%;max-width:${isVertical ? 880 : 1180}px;">
    ${parsedItems.map((it, i) => `<div id="${cardIds[i]}" style="display:flex;align-items:center;gap:18px;padding:20px 24px;border-radius:22px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.14)};border-left:4px solid ${accent};box-shadow:0 16px 38px rgba(0,0,0,0.32);">
      <div style="width:48px;height:48px;min-width:48px;border-radius:14px;background:${withAlpha(accent, 0.18)};border:1.5px solid ${withAlpha(accent, 0.55)};display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardIdxSize}px;color:${accent};">${String(i + 1).padStart(2, "0")}</div>
      <div style="flex:1;display:flex;flex-direction:column;gap:6px;min-width:0;">
        <div style="display:inline-flex;align-self:flex-start;align-items:center;gap:8px;padding:4px 12px;border-radius:9999px;background:${withAlpha(accent, 0.15)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardTagSize}px;letter-spacing:2.5px;text-transform:uppercase;color:${accent};">${escapeHtml(kategorieTag)}</div>
        <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${cardTitleSize}px;color:${themeColors.fg};line-height:1.2;">${escapeHtml(it.title)}</div>
      </div>
      <svg width="${Math.round(cardTitleSize * 0.95)}" height="${Math.round(cardTitleSize * 0.95)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
    </div>`).join("")}
  </div>`;

  const init = cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, x: 60 })).join("\n");

  const tweens: string[] = [];
  cardIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, x: 0, duration: 0.5, ease: "raw:\"power3.out\"" }, 0.55 + i * 0.18));
  });

  const ctaAt = 0.7 + cardIds.length * 0.18;
  tweens.push(t.to(`#${ctaId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, ctaAt));
  tweens.push(t.to(`#${ctaGlowId}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, ctaAt + 0.1));
  const dwell = Math.max(0.6, clipDurationSec - ctaAt - 1.2);
  tweens.push(t.to(`#${ctaGlowId}`, { opacity: 0.4, duration: dwell, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, ctaAt + 0.6));

  // Wir verwenden ctx hier nicht, aber halten die Signatur einheitlich für ListLayoutCtx.
  void ctx;

  return { html, init, tweens };
}

// ============================================================================
// ACCORDION LIST — Items klappen sequenziell auf, zeigen Details, klappen zu.
// ============================================================================
// Layout:
//   • Header (außerhalb, vom Parent gesetzt).
//   • Item-Spalte: jedes Item ist eine Glass-Card mit fixer "Bar"-Höhe
//     (Index + PRODUKT-Pill + Titel + Chevron) PLUS ein collapsible
//     "Detail"-Panel (Beschreibung + Preis + optionales Bild).
//   • Animation: ein Item ist immer "aktiv". Sein Detail-Panel hat
//     volle Höhe & Opacity, die Bar leuchtet (Accent-Border-Glow). Andere
//     Items sind kollabiert (Detail height:0, opacity:0). Nach `holdSec`
//     wechselt der aktive Index → nächstes Item.
//   • CTA-Pill erscheint NACH dem letzten Aufklappen.
// ============================================================================
function buildAccordionList(L: ListLayoutCtx): Fragment {
  const { isVertical, accent, ctx, t, parsedItems, kategorieTag, listId,
    cardIds, ctaId, ctaGlowId, cardTitleSize, cardTagSize, cardIdxSize,
    clipDurationSec } = L;

  // Fixierte Höhen — `auto` lässt sich in der pre-built GSAP-Pipeline nicht
  // zuverlässig animieren, deshalb hartcodierte Pixel.
  const collapsedH = isVertical ? 76 : 70;   // nur die Bar
  const expandedH  = isVertical ? 230 : 200; // Bar + Detail-Panel
  const gap = 12;

  // Aktivierungs-Choreografie
  // Wir verteilen die N Items über die verfügbare Restzeit (minus
  // Header-Intro + CTA-Outro). Default ~2.6s pro Item, min 1.8s.
  const introAt = 0.45;          // erste Bar erscheint
  const stagger = 0.16;          // Bars staggern rein
  const firstExpandAt = introAt + parsedItems.length * stagger + 0.3;
  const ctaTailReserve = 1.6;
  const remaining = Math.max(1.0, clipDurationSec - firstExpandAt - ctaTailReserve);
  const perItem = Math.max(1.8, Math.min(3.2, remaining / Math.max(1, parsedItems.length)));

  const detailIds = cardIds.map((id) => `${id}-detail`);
  const barIds    = cardIds.map((id) => `${id}-bar`);
  const chevronIds = cardIds.map((id) => `${id}-chev`);

  const html = `<div id="${listId}" style="display:flex;flex-direction:column;gap:${gap}px;width:100%;max-width:${isVertical ? 880 : 1080}px;">
    ${parsedItems.map((it, i) => {
      const idxStr = String(i + 1).padStart(2, "0");
      const hasDetail = Boolean(it.description || it.price || it.imageUrl);
      const detailHtml = hasDetail ? `<div id="${detailIds[i]}" style="display:flex;flex-direction:column;gap:10px;padding:14px 22px 18px 22px;opacity:0;overflow:hidden;height:0;">
        ${it.price ? `<div style="display:flex;align-items:center;gap:14px;flex-wrap:wrap;">
          <span style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardTitleSize * 1.2}px;line-height:1;letter-spacing:-1px;background:linear-gradient(135deg, ${accent} 0%, ${accent} 55%, ${withAlpha(accent, 0.78)} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;">${escapeHtml(it.price)}</span>
        </div>` : ""}
        ${it.description ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${cardTitleSize * 0.78}px;line-height:1.45;color:${withAlpha(themeColors.fg, 0.78)};text-wrap:balance;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;">${escapeHtml(it.description)}</div>` : ""}
        ${it.imageUrl ? `<div style="margin-top:4px;width:100%;max-width:520px;height:${isVertical ? 110 : 96}px;border-radius:14px;overflow:hidden;background:${withAlpha(themeColors.fg, 0.06)};border:1px solid ${withAlpha(themeColors.fg, 0.12)};">
          <img src="${escapeHtml(it.imageUrl)}" alt="${escapeHtml(it.title)}" style="width:100%;height:100%;object-fit:cover;display:block;" />
        </div>` : ""}
      </div>` : "";

      return `<div id="${cardIds[i]}" style="position:relative;width:100%;border-radius:22px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.14)};border-left:4px solid ${withAlpha(accent, 0.45)};box-shadow:0 16px 38px rgba(0,0,0,0.32);overflow:hidden;height:${collapsedH}px;">
        <div id="${barIds[i]}" style="display:flex;align-items:center;gap:18px;padding:0 22px;height:${collapsedH}px;">
          <div style="width:44px;height:44px;min-width:44px;border-radius:12px;background:${withAlpha(accent, 0.18)};border:1.5px solid ${withAlpha(accent, 0.55)};display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardIdxSize}px;color:${accent};">${idxStr}</div>
          <div style="flex:1;display:flex;align-items:center;gap:12px;min-width:0;">
            <div style="display:inline-flex;align-items:center;gap:8px;padding:3px 10px;border-radius:9999px;background:${withAlpha(accent, 0.15)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardTagSize * 0.92}px;letter-spacing:2.5px;text-transform:uppercase;color:${accent};white-space:nowrap;flex:0 0 auto;">${escapeHtml(kategorieTag)}</div>
            <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${cardTitleSize}px;color:${themeColors.fg};line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1;min-width:0;">${escapeHtml(it.title)}</div>
          </div>
          <svg id="${chevronIds[i]}" width="${Math.round(cardTitleSize * 0.9)}" height="${Math.round(cardTitleSize * 0.9)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;transform-origin:50% 50%;"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
        ${detailHtml}
      </div>`;
    }).join("")}
  </div>`;

  const init: string[] = [];
  cardIds.forEach((id, i) => {
    init.push(gsapSetLine(`#${id}`, { opacity: 0, y: 18 }));
    init.push(gsapSetLine(`#${detailIds[i]}`, { height: 0, opacity: 0 }));
    init.push(gsapSetLine(`#${chevronIds[i]}`, { rotation: 0 }));
  });

  const tweens: string[] = [];
  // Bars staggern rein
  cardIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power3.out\"" }, introAt + i * stagger));
  });

  // Sequenzielles Aufklappen
  parsedItems.forEach((it, i) => {
    const hasDetail = Boolean(it.description || it.price || it.imageUrl);
    const at = firstExpandAt + i * perItem;
    const closeAt = at + perItem - 0.35;
    const targetH = hasDetail ? expandedH : collapsedH;
    const activeShadow = `0 22px 48px ${withAlpha(accent, 0.35)}`;

    // Aktive Card: Border-Left zu vollem Accent (immer), Höhe nur wenn Details existieren.
    tweens.push(t.to(`#${cardIds[i]}`, {
      height: targetH,
      duration: 0.55,
      ease: "raw:\"power3.out\"",
      borderLeftColor: accent,
      boxShadow: activeShadow,
    }, at));
    if (hasDetail) {
      tweens.push(t.to(`#${detailIds[i]}`, { height: expandedH - collapsedH, opacity: 1, duration: 0.55, ease: "raw:\"power3.out\"" }, at));
      tweens.push(t.to(`#${chevronIds[i]}`, { rotation: 180, duration: 0.45, ease: "raw:\"power3.out\"" }, at));
    } else {
      // Ohne Details: nur Chevron leicht "winken" (visuelles Feedback)
      tweens.push(t.to(`#${chevronIds[i]}`, { x: 4, duration: 0.4, ease: "raw:\"power2.out\"" }, at));
      tweens.push(t.to(`#${chevronIds[i]}`, { x: 0, duration: 0.4, ease: "raw:\"power2.in\"" }, at + 0.4));
    }

    // Bevor das nächste Item dran ist (oder am Ende), wieder "kollabieren".
    const isLast = i === parsedItems.length - 1;
    if (!isLast) {
      tweens.push(t.to(`#${cardIds[i]}`, {
        height: collapsedH,
        duration: 0.45,
        ease: "raw:\"power3.in\"",
        borderLeftColor: withAlpha(accent, 0.45),
        boxShadow: "0 16px 38px rgba(0,0,0,0.32)",
      }, closeAt));
      if (hasDetail) {
        tweens.push(t.to(`#${detailIds[i]}`, { height: 0, opacity: 0, duration: 0.45, ease: "raw:\"power3.in\"" }, closeAt));
        tweens.push(t.to(`#${chevronIds[i]}`, { rotation: 0, duration: 0.4, ease: "raw:\"power3.in\"" }, closeAt));
      }
    }
  });

  // CTA-Pill kommt am Ende
  const ctaAt = firstExpandAt + parsedItems.length * perItem + 0.15;
  tweens.push(t.to(`#${ctaId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, ctaAt));
  tweens.push(t.to(`#${ctaGlowId}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, ctaAt + 0.1));
  const dwell = Math.max(0.6, clipDurationSec - ctaAt - 0.8);
  tweens.push(t.to(`#${ctaGlowId}`, { opacity: 0.4, duration: dwell, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, ctaAt + 0.6));

  void ctx;

  return { html, init: init.join("\n"), tweens };
}
